import React, { useState } from 'react';
import { Alert, Button, Card, List, Select, Space, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const { Text, Paragraph } = Typography;

const KINDS = [
  { value: 'project', label: 'Project' },
  { value: 'executive', label: 'Executive' },
  { value: 'delay_summary', label: 'Delay summary' },
  { value: 'constraint_summary', label: 'Constraint summary' },
];

type Props = { projectId?: string };

export const FieldAiPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [kind, setKind] = useState('project');

  const briefQ = useQuery({
    queryKey: ['cep-field-ai', projectId, kind],
    queryFn: () => epcService.getCepFieldAi(projectId, { kind }),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const genMut = useMutation({
    mutationFn: () =>
      epcService.generateFieldAi({
        projectId,
        kind,
        persist: true,
        suggestActions: false,
      }),
    onSuccess: () => {
      message.success('Field AI brief generated (grounded / advisory)');
      void qc.invalidateQueries({ queryKey: ['cep-field-ai'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for Field AI</Tag>;

  const data = (briefQ.data || {}) as any;
  const content = data.content || data.content_json || data;
  const headlines: string[] = content.headlines || [];
  const actions: Array<{ title: string; reason: string }> = content.suggested_actions || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          style={{ width: 200 }}
          value={kind}
          options={KINDS}
          onChange={setKind}
        />
        <Button type="primary" loading={genMut.isPending} onClick={() => genMut.mutate()}>
          Generate brief
        </Button>
        <Tag color="geekblue">CEP Wave 6</Tag>
        <Tag>Grounded only — no ERP bypass</Tag>
      </Space>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Advisory Field AI"
        description="Summaries are computed from CEP packages, constraints, delays, progress, and productivity. Elevated items soft-publish to EOIP; EOAF actions stay suggested."
      />
      <Card size="small" title="Headlines" loading={briefQ.isLoading}>
        {headlines.length ? (
          <List
            size="small"
            dataSource={headlines}
            renderItem={(h) => <List.Item>{h}</List.Item>}
          />
        ) : (
          <Text type="secondary">{content.message || content.summary || 'No brief yet — generate one.'}</Text>
        )}
        {content.summary && <Paragraph style={{ marginTop: 8 }}>{content.summary}</Paragraph>}
      </Card>
      <Card size="small" title="Suggested actions (advisory)" style={{ marginTop: 12 }}>
        <List
          size="small"
          dataSource={actions}
          locale={{ emptyText: 'No open constraints/delays driving actions' }}
          renderItem={(a) => (
            <List.Item>
              <Space direction="vertical" size={0}>
                <Text strong>{a.title}</Text>
                <Text type="secondary">{a.reason}</Text>
              </Space>
            </List.Item>
          )}
        />
      </Card>
    </div>
  );
};

export default FieldAiPanel;
